import { Combination, Permutation } from "js-combinatorics";
import Mexp from "math-expression-evaluator";
import { getLastString } from "../../lib/util/getLastWord";
import { expandBracketedString } from "../../lib/wordGenerator/bracketedString";
import { kanaToRoman } from "../../lib/wordGenerator/kanaToRoman";
import { replaceSign } from "../../lib/wordGenerator/replaceSign";
import { romajiToHira } from "../../lib/wordGenerator/romajiToHira";
import { textLength } from "../../lib/wordGenerator/textLength";
import {
  listPlaceholderCharacter,
  listTerminateCharacter,
} from "../../param/generalSearchQuery";
import { ListDefinition } from "../../param/listDefinitionType";
import { Trace } from "./generalSearchResult";

// 1つのリストで文字を前後させる変換関数
const rotateConvert = (
  input: string,
  listDefinitionsTable: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.list.regex;

  const replaced = input.replace(
    regex,
    (match, stringPart, listName: string, _shift: string, option: string) => {
      const listContent = listName
        .split("&")
        .map((listName) => listDefinitionsTable[listName]?.content ?? listName)
        .join(listTerminateCharacter);
      const contents = listContent.split(listTerminateCharacter);
      option = option ?? "";
      const loop = option.includes("l");

      const shifts: number[] = [];

      _shift.split("&").forEach((__shift) => {
        if (__shift.includes("~")) {
          // a~b -> a to b
          const [a, b] = __shift.split("~").map((e) => parseInt(e));
          for (let i = a; i <= b; i++) {
            shifts.push(i);
          }
        } else {
          shifts.push(parseInt(__shift));
        }
      });

      if (!listContent) {
        return "";
      }
      const stringArr = (stringPart as string).split("");
      const ret = shifts
        .map((shift) => {
          return stringArr
            .map((c) => {
              const nexts: string[] = [];
              contents.forEach((content) => {
                let searchIndex = content.indexOf(c);
                while (searchIndex !== -1) {
                  const next = loop
                    ? (searchIndex + shift) % content.length
                    : searchIndex + shift;

                  if (
                    next >= 0 &&
                    next < content.length &&
                    content[next] !== listPlaceholderCharacter
                  ) {
                    nexts.push(content[next]);
                  }
                  searchIndex = content.indexOf(c, searchIndex + 1);
                }
              });
              return "[" + nexts.join("") + "]";
            })
            .join("");
        })
        .join("\n");
      return ret;
    }
  );
  return replaced.split("\n");
};

const stringToCharArr = (str: string) => {
  if (str.match(/[^{}\[\]]/)) {
    if (str.match(/\|/)) {
      return str.split("|");
    }
    return Array.from(str);
  }
  const { c2Arr } = Array.from(str).reduce(
    (a, b) => {
      if (a.bracketDepth === 0 && b === "|") {
        if (!a.hasPipe) {
          const joinedC2Arr = a.c2Arr.join("");
          return {
            bracketDepth: 0,
            hasPipe: true,
            c2Arr: [joinedC2Arr, ""],
          };
        } else {
          return {
            ...a,
            c2Arr: [...a.c2Arr, ""],
          };
        }
      }
      if (!a.hasPipe && a.bracketDepth === 0) {
        a.c2Arr.push("");
      }

      if (b === "{") {
        a.bracketDepth += 1;
      } else if (b === "}") {
        a.bracketDepth -= 1;
      } else if (b === "[") {
        a.bracketDepth += 1;
      } else if (b === "]") {
        a.bracketDepth -= 1;
      }
      a.c2Arr[a.c2Arr.length - 1] += b;
      return a;
    },
    {
      hasPipe: false,
      bracketDepth: 0,
      c2Arr: [] as string[],
    }
  );
  return c2Arr;
};

// 2つのListの共通位置の文字列に置き換える変換関数
const pairConvert = (
  input: string,
  listDefinitionsTable: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.pair.regex;
  const replaced = input.replace(
    regex,
    (_, stringPart, listName1: string, listName2: string, option) => {
      const listContent1 = listName1
        .split("&")
        .map((listName) => listDefinitionsTable[listName]?.content ?? listName)
        .join(";");
      const listContent2 = listName2
        .split("&")
        .map((listName) => listDefinitionsTable[listName]?.content ?? listName)
        .join(";");

      option = option ?? "";
      if (!listContent1 || !listContent2) {
        return "";
      }
      const isAny = option.includes("a");
      const isLeastOnce = option.includes("o");
      const leastCounter = parseInt(option);

      if (!isNaN(leastCounter)) {
      }

      const contents1 = listContent1.split(listTerminateCharacter);
      const contents2 = listContent2.split(listTerminateCharacter);
      const contents2Arr = contents2.map((c2Line) => {
        return stringToCharArr(c2Line);
      });
      const stringArr = (stringPart as string).split("");
      const ret = stringArr
        .map((c) => {
          const nexts: string[] = [];
          contents1.forEach((content1, i) => {
            let searchIndex1 = content1.indexOf(c);
            while (searchIndex1 !== -1) {
              const c2Arr = contents2Arr[i];

              if (
                c2Arr.length <= searchIndex1 ||
                c2Arr[searchIndex1] === listPlaceholderCharacter
              ) {
                return "";
              }
              const next = c2Arr[searchIndex1];
              if (next !== listPlaceholderCharacter) {
                nexts.push(next);
              }
              searchIndex1 = content1.indexOf(c, searchIndex1 + 1);
            }
          });
          if (isAny && nexts.length === 0) {
            return c;
          }
          if (nexts.length === 1) {
            return nexts[0];
          }
          return "[" + nexts.join("|") + "]";
        })
        .join("");
      if (isLeastOnce && ret === stringPart) {
        return "[]";
      }
      return ret;
    }
  );
  return replaced;
};

const reverseConvert = (
  input: string,
  _lists: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.reverse.regex;

  const replaced = input.replace(regex, (_, stringPart) => {
    return (stringPart as string).split("").reverse().join("");
  });
  return replaced;
};

const sortConvert = (
  input: string,
  _: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.sort.regex;

  const replaced = input.replace(regex, (_, stringPart) => {
    return (stringPart as string).split("").sort().join("");
  });
  return replaced;
};

const romaConvert = (
  input: string,
  _: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.roma.regex;

  const replaced = input.replace(regex, (_, stringPart, option) => {
    option = option ?? "";
    const longSound = option.includes("l");
    const kunrei = option.includes("k");
    const typing = option.includes("t");

    const type = kunrei
      ? typing
        ? "kunreiTyping"
        : "kunrei"
      : typing
      ? "hepburnTyping"
      : "hepburn";

    return kanaToRoman(stringPart, type, {
      longSound,
      bmp: true,
    });
  });
  return replaced;
};

const kanaConvert = (
  input: string,
  _lists: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.kana.regex;
  const replaced = input.replace(regex, (_, stringPart) => {
    return romajiToHira(stringPart as string);
  });
  return replaced;
};

const calculateConvert = (
  input: string,
  _lists: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.calc.regex;
  const mexp = new Mexp();
  try {
    const replaced = input.replace(regex, (_, stringPart) => {
      const replacedLine = replaceSign(stringPart);
      return "" + mexp.eval(replacedLine, [], {});
    });
    return replaced;
  } catch (e) {
    return "";
  }
};

const listContentConvert = (
  input: string,
  lists: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.listContent.regex;

  const replaced = input.replace(regex, (_, stringPart) => {
    if (!stringPart || !lists || !lists[stringPart]) {
      return "";
    }
    const listContent = stringPart
      .split("&")
      .map(
        (listName: string) => lists[listName]?.content.replace(/;#/g, "") ?? ""
      )
      .join("");
    return listContent;
  });
  return replaced;
};

const traceConvert = (
  input: string,
  _: Record<string, ListDefinition>,
  trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.trace.regex;

  const replaced = input.replace(regex, (_, stringPart) => {
    const index = parseInt(stringPart);
    return index > 0
      ? getLastString(trace[index - 1] ?? "")
      : getLastString(trace[trace.length + index - 1] ?? "");
  });
  return replaced;
};

const subStrConvert = (
  input: string,
  _: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.substr.regex;

  const replaced = input.replace(regex, (_, stringPart, option) => {
    const [_min, _max] = option.includes("~")
      ? option.split("~").map((e: string) => parseInt(e))
      : [parseInt(option), parseInt(option)];
    const [min, max] = [
      _min <= 0 ? textLength(stringPart) + _min : _min,
      _max <= 0 ? textLength(stringPart) + _max : _max,
    ].sort((a, b) => a - b);

    const result = [];
    for (let i = min; i <= max; i++) {
      for (let j = 0; j <= (stringPart as string).length - i; j++) {
        result.push((stringPart as string).substring(j, j + i));
      }
    }
    if (result.length === 1) {
      return result[0];
    }
    return `[${result.join("|")}]`;
  });
  return replaced;
};

const subSeqConvert = (
  input: string,
  _: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.subseq.regex;

  const replaced = input.replace(regex, (_, stringPart, option) => {
    const [_min, _max] = option.includes("~")
      ? option.split("~").map((e: string) => parseInt(e))
      : [parseInt(option), parseInt(option)];
    const [min, max] = [
      _min <= 0 ? textLength(stringPart) + _min : _min,
      _max <= 0 ? textLength(stringPart) + _max : _max,
    ].sort((a, b) => a - b);

    let result: string[] = [];
    for (let i = min; i <= max; i++) {
      const comb = [...new Combination(stringPart as string, i)].map((e) =>
        e.join("")
      );
      result = result.concat(comb);
    }
    return `[${result.join("|")}]`;
  });
  return replaced;
};

const permutationConvert = (
  input: string,
  _: Record<string, ListDefinition>,
  _trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
) => {
  const regex = functions.perm.regex;

  const replaced = input.replace(regex, (_, stringPart, option) => {
    const textLength = (s: string) => s.length;
    const [_min, _max] =
      !option || option === ""
        ? [textLength(stringPart), textLength(stringPart)]
        : option.includes("~")
        ? option.split("~").map((e: string) => parseInt(e))
        : [parseInt(option), parseInt(option)];
    const [min, max] = [
      _min <= 0 ? textLength(stringPart) + _min : _min,
      _max <= 0 ? textLength(stringPart) + _max : _max,
    ].sort((a, b) => a - b);

    let result: string[] = [];
    for (let i = min; i <= max; i++) {
      const comb = [...new Permutation(stringPart as string, i)].map((e) =>
        e.join("")
      );
      result = result.concat(comb);
    }
    return `[${result.join("|")}]`;
  });
  return replaced;
};

type FuncDefinition = {
  regex: RegExp;
  func: (
    input: string,
    listDefinitionsTable: Record<string, ListDefinition>,
    trace: Trace[],
    functions: Record<string, FuncDefinition>,
    ...args: string[]
  ) => string | string[];
};

const replacedPatternFunctions: Record<string, FuncDefinition> = {
  listContent: {
    regex: /<([^\(\)\<\>\[\]]+)>/g,
    func: listContentConvert,
  },
  list: {
    regex:
      /rotate\(\s*([^\s,()]*?)\s*,\s*([^\s,()]+)\s*,\s*([0-9-~&]+)\s*(?:,\s*([^()]*)\s*)?\s*\)/g,
    func: rotateConvert,
  },
  pair: {
    regex:
      /pair\(\s*([^\s,()]*?)\s*,\s*([^\s,()]+)\s*,\s*([^\s,()]+)\s*(?:,\s*([^()]*)\s*)?\s*\)/g,
    func: pairConvert,
  },
  reverse: { regex: /reverse\(\s*([^()]*?)\s*\)/g, func: reverseConvert },
  sort: { regex: /sort\(\s*([^()]*?)\s*\)/g, func: sortConvert },
  kana: { regex: /kana\(\s*([^()]*?)\s*\)/g, func: kanaConvert },
  roma: {
    regex: /roma\(\s*([^()]*?)\s*(?:,\s*([^()]*)\s*)?\)/g,
    func: romaConvert,
  },
  trace: { regex: /trace\(\s*([^()]*?)\s*\)/g, func: traceConvert },
  substr: {
    regex: /substr\(\s*([^()]*?)\s*(?:,\s*([^()]*)\s*)\)/g,
    func: subStrConvert,
  },
  subseq: {
    regex: /subseq\(\s*([^()]*?)\s*(?:,\s*([^()]*)\s*)\)/g,
    func: subSeqConvert,
  },
  perm: {
    regex: /perm\(\s*([^()]*?)\s*(?:,\s*([^()]*)\s*)?\)/g,
    func: permutationConvert,
  },
  calc: {
    regex: /@{([^{}]*?)}/g,
    func: calculateConvert,
  },
};

const searchPatternFunctions: Record<string, FuncDefinition> = {
  listContent: {
    regex: /@<([^()>]+)>/g,
    func: listContentConvert,
  },
  trace: { regex: /@trace\(\s*([^()]*?)\s*\)/g, func: traceConvert },
};

const processInnermostFunction = (
  str: string,
  listDefinitionsTable: Record<string, ListDefinition>,
  trace: Trace[],
  functions: Record<string, FuncDefinition> = replacedPatternFunctions
): [string[], boolean] => {
  for (const { regex, func } of Object.values(functions)) {
    const match = str.match(regex);
    if (match) {
      const [fullMatch, ...args] = match;
      const replaced = func(fullMatch, listDefinitionsTable, trace, functions);

      if (Array.isArray(replaced)) {
        // replacedがstring[]の場合、各文字列でfullMatchを置き換える
        const replacedStrings = replaced.map((r) => str.replace(fullMatch, r));
        return [replacedStrings, true];
      } else {
        // replacedがstringの場合
        return [[str.replace(fullMatch, replaced)], true];
      }
    }
  }
  return [[str], false];
};

export const processNestedFunctions = (
  str: string,
  listDefinitionsTable: Record<string, ListDefinition>,
  trace: Trace[]
) => {
  let modified = false;
  let strs = [str];
  do {
    const ret = strs.map((str) =>
      processInnermostFunction(
        str,
        listDefinitionsTable,
        trace ?? [],
        replacedPatternFunctions
      )
    );
    if (ret.length === 0) {
      return [];
    }
    modified = ret.map((e) => e[1]).reduce((a, b) => a || b);
    strs = ret.map((e) => e[0]).flat();
    strs = strs.map((st) => expandBracketedString(st)).flat();
  } while (modified);
  return strs;
};

export const processSearchPatternFunctions = (
  str: string,
  listDefinitionsTable: Record<string, ListDefinition>
) => {
  let modified = false;
  let strs = [str];
  do {
    const ret = strs.map((str) =>
      processInnermostFunction(
        str,
        listDefinitionsTable,
        [],
        searchPatternFunctions
      )
    );
    if (ret.length === 0) {
      return [];
    }
    modified = ret.map((e) => e[1]).reduce((a, b) => a || b);
    strs = ret.map((e) => e[0]).flat();
  } while (modified);
  return strs;
};
